import { useMemo } from 'react';
import { useMantineColorScheme, useMantineTheme } from '@mantine/core';
import {
  ROAD_OPACITY_DARK,
  ROAD_OPACITY_LIGHT,
  PIN_OPACITY_DARK,
  PIN_OPACITY_LIGHT,
} from '@/constants/mapIllustration';
import type { RoadOpacity, PinOpacity } from '@/constants/mapIllustration';

export interface MapIllustrationTheme {
  isDark: boolean;
  bgColor: string;
  gridColor: string;
  gridWidth: string;
  accent: string;
  dashColor: string;
  glowStart: string;
  glowEnd: string;
  roadOp: RoadOpacity;
  pinOp: PinOpacity;
  prefersReducedMotion: boolean;
}

/**
 * Shared theme derivation for decorative map illustration SVGs.
 * Used by OnboardingMapIllustration and the share gate / error illustrations.
 */
export function useMapIllustrationTheme(): MapIllustrationTheme {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const isDark = colorScheme === 'dark';

  const prefersReducedMotion = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  return useMemo(() => {
    const bgColor = isDark ? theme.colors.dark[8] : theme.colors.gray[0];
    const gridColor = isDark ? theme.colors.dark[5] : theme.colors.gray[2];
    const gridWidth = isDark ? '0.5' : '0.7';
    const accent = isDark ? theme.colors.orange[6] : theme.colors.indigo[6];
    const dashColor = isDark ? 'rgba(255,255,255,0.15)' : 'white';
    const glowStart = theme.colors.indigo[0];
    const glowEnd = theme.white;
    const roadOp = isDark ? ROAD_OPACITY_DARK : ROAD_OPACITY_LIGHT;
    const pinOp = isDark ? PIN_OPACITY_DARK : PIN_OPACITY_LIGHT;

    return {
      isDark,
      bgColor,
      gridColor,
      gridWidth,
      accent,
      dashColor,
      glowStart,
      glowEnd,
      roadOp,
      pinOp,
      prefersReducedMotion,
    };
  }, [isDark, theme, prefersReducedMotion]);
}
